import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LinkChallengeInfo } from '@draughts/shared';
import { fetchChallengeLink } from '../../services/challenge-api';
import { acceptLinkChallenge, releaseLinkChallenge } from '../../services/socket';
import { useAuthStore } from '../../store/auth-store';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { useOnlineStore } from '../../store/online-store';
import { trackChallengeLinkAccepted } from '../../utils/analytics';
import { codiceErrore } from '../../utils/codice-errore';
import { chiaveErroreOnline, CHIAVI_ERRORE_ONLINE } from '../../utils/chiave-errore-online';
import { log } from '../../utils/logger';
import ChallengeDetails from './ChallengeDetails';
import './ChallengeInvitePage.css';

interface ChallengeInvitePageProps {
  linkId: string;
  /** Start a guest session (App.handlePlayAsGuest) — the no-signup path. */
  /** Vedi `handlePlayAsGuest`: chiave i18n dell'errore, o `null` se è andata. */
  onPlayAsGuest: () => Promise<string | null>;
  /** Open the auth modal for a friend who has (or wants) an account. */
  onLoginRequired: () => void;
  /** Leave the invite and go back to the home screen. */
  onLeave: () => void;
}

type Phase = 'loading' | 'ready' | 'accepting' | 'waiting' | 'unavailable';

/**
 * The screen a friend lands on from a challenge link (`/?challenge=<id>`),
 * usually straight out of WhatsApp and usually logged out.
 *
 * Two things it has to get right:
 *  - accepting must not require an account. "Gioca subito" opens a guest session
 *    and accepts by itself as soon as the socket is up (the game then downgrades
 *    to casual, which is stated up front when the challenge was rated).
 *  - the challenger is often NOT online at this moment (they are in WhatsApp).
 *    Then the claim is held server-side and this screen waits: `game:start`
 *    arrives on its own when they come back, and App navigates to the board.
 */
function ChallengeInvitePage({ linkId, onPlayAsGuest, onLoginRequired, onLeave }: ChallengeInvitePageProps) {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  /*
   * L'accesso e' stato DECISO, non solo tentato.
   *
   * `accessToken` non e' persistito (`partialize` salva solo `user`,
   * `isAuthenticated`, `isGuest`), quindi al montaggio il token va rifatto via
   * rete da `checkAuth()`. Nella finestra in cui quella e' in volo,
   * `isAuthenticated` puo' essere ancora falso per un utente REGISTRATO: e'
   * proprio la regola che `App.tsx` dichiara — «gate on isAuthReady, never on
   * isAuthenticated alone» — e che questo punto violava.
   *
   * Aprendo una sfida da un link con la rete lenta e toccando «Accetta» entro
   * un paio di secondi si finiva a `loginAsGuest()`, che riscrive il cookie di
   * sessione sullo stesso nome: la sessione registrata veniva distrutta senza
   * un logout, e la partita si giocava come ospite e non valutata.
   */
  const isAuthReady = useAuthStore((s) => s.isAuthReady);
  const isGuest = useAuthStore((s) => s.isGuest);
  const socketStatus = useMultiplayerStore((s) => s.status);
  const notice = useOnlineStore((s) => s.linkChallengeNotice);
  const setPendingLinkChallenge = useOnlineStore((s) => s.setPendingLinkChallenge);
  const setLinkChallengeNotice = useOnlineStore((s) => s.setLinkChallengeNotice);

  const [link, setLink] = useState<LinkChallengeInfo | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  /** Chiave i18n dell'errore dell'ingresso da ospite (vedi `entraComeOspite`). */
  const [erroreOspite, setErroreOspite] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Set when Accept was pressed before there was a session/socket to accept with. */
  const [pendingAccept, setPendingAccept] = useState(false);
  const acceptingRef = useRef(false);

  // Load the invite. Public endpoint, so this works logged out.
  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    void fetchChallengeLink(linkId).then((info) => {
      if (cancelled) return;
      if (!info) {
        setPhase('unavailable');
        return;
      }
      setLink(info);
      const dead = info.status !== 'open' && info.status !== 'claimed';
      setPhase(dead || info.expiresAt <= Date.now() ? 'unavailable' : 'ready');
    });
    return () => { cancelled = true; };
  }, [linkId]);

  // The link died under a waiting friend (challenger cancelled it, or it aged out).
  useEffect(() => {
    if (!notice) return;
    setPhase('unavailable');
  }, [notice]);

  // Leaving the screen must not leave a stale notice behind for the next visit.
  useEffect(() => () => { setLinkChallengeNotice(null); }, [setLinkChallengeNotice]);

  const accept = useCallback(async () => {
    if (acceptingRef.current) return;
    acceptingRef.current = true;
    setPhase('accepting');
    setError(null);
    try {
      const result = await acceptLinkChallenge(linkId);
      trackChallengeLinkAccepted(link?.variant);
      if (result.roomId) {
        // game:start has already arrived — App switches to the board.
        log.info('ChallengeInvitePage', 'Link accepted, game starting', { roomId: result.roomId });
        return;
      }
      // Challenger offline: hold the spot and wait for them.
      log.info('ChallengeInvitePage', 'Link claimed, waiting for challenger');
      setPendingLinkChallenge(result.link ?? link);
      setPhase('waiting');
    } catch (err) {
      /*
       * Se il guasto ha un CODICE si tiene quello, e a schermo va la
       * traduzione: e' cosi' che «Not connected to server» — la frase inglese
       * dei log, che usciva di qui quando il socket era giu' — smette di
       * arrivare sotto gli occhi di chi legge.
       *
       * Senza codice si tiene il messaggio com'era: i gestori delle sfide via
       * link rispondono con frasi inglesi e non con codici
       * (`link-challenge.handler.ts`), e sostituirle con un «qualcosa non ha
       * funzionato» toglierebbe l'unica informazione che oggi c'e'. Finirlo
       * vuol dire dare dei codici a quei rifiuti, lato server: e' un lavoro suo.
       */
      setError(codiceErrore(err) ?? (err instanceof Error ? err.message : String(err)));
      setPhase('ready');
    } finally {
      acceptingRef.current = false;
    }
  }, [linkId, link, setPendingLinkChallenge]);

  // Deferred accept: the button was pressed while logged out, so it runs as soon
  // as the guest (or freshly logged-in) session has its socket connected.
  useEffect(() => {
    if (!pendingAccept || !isAuthenticated || socketStatus !== 'connected') return;
    setPendingAccept(false);
    void accept();
  }, [pendingAccept, isAuthenticated, socketStatus, accept]);

  /*
   * «Accetta» premuto prima che l'accesso sia deciso non resta appeso.
   *
   * `handleAccept` mette comunque `pendingAccept` e la schermata in
   * 'accepting', ma la sessione ospite parte solo ad accesso deciso. L'effetto
   * che consuma `pendingAccept` (qui sopra) pretende `isAuthenticated`, che per
   * un visitatore non diventera' mai vero da solo: senza questa ripresa la
   * pagina restava su una rotella infinita, e se ne usciva solo ricaricando.
   */
  useEffect(() => {
    if (!pendingAccept || !isAuthReady || isAuthenticated) return;
    void entraComeOspite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAccept, isAuthReady, isAuthenticated]);

  const handleAccept = () => {
    if (isAuthenticated && socketStatus === 'connected') {
      void accept();
      return;
    }
    setPendingAccept(true);
    setPhase('accepting');
    /* Vedi `isAuthReady`: mai dedurre «non e' loggato» da un accesso ancora in
       corso. Se non si sa ancora, ci pensa l'effetto qui sotto appena si sa. */
    if (isAuthReady && !isAuthenticated) void entraComeOspite();
  };

  /*
   * L'ingresso da ospite, con il fallimento DETTO.
   *
   * Chi apre un link di sfida senza account passa di qui, e finora un
   * fallimento (tipicamente il limitatore: 3 accessi ospite ogni 15 minuti per
   * IP, cioè niente dietro un CGNAT) lasciava la pagina sulla rotella
   * «accettazione in corso» per sempre: `pendingAccept` aspetta un
   * `isAuthenticated` che non sarebbe mai arrivato. Ora si torna a `'ready'`,
   * si smette di aspettare e si dice perché — con la via d'uscita già in
   * pagina, il bottone «ho già un account».
   */
  const entraComeOspite = async (): Promise<void> => {
    const chiaveErrore = await onPlayAsGuest();
    if (!chiaveErrore) return;
    setPendingAccept(false);
    setPhase('ready');
    setErroreOspite(chiaveErrore);
  };

  const handleLeaveWaiting = () => {
    void releaseLinkChallenge(linkId).catch(() => { /* link already gone — nothing to release */ });
    setPendingLinkChallenge(null);
    onLeave();
  };

  const linkMatchType = link ? (link.matchType ?? (link.rated ? 'any' : 'casual')) : null;
  const notRegistered = !isAuthenticated || isGuest;
  // "Solo valida" is registered humans only: the server refuses the accept, so
  // the button is barred here and the note turns into a reason to sign in.
  const registeredOnly = linkMatchType === 'rated' && notRegistered;
  // An 'any' challenge still lets them in — it simply will not count.
  const guestCasual = linkMatchType === 'any' && notRegistered;

  if (phase === 'loading') {
    return (
      <div className="challenge-invite-page">
        <div className="challenge-invite-card">
          <div className="challenge-invite-spinner" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (phase === 'unavailable') {
    return (
      <div className="challenge-invite-page">
        <div className="challenge-invite-card">
          <h2>{t('online.challenge.link.unavailableTitle')}</h2>
          <p className="challenge-invite-note">
            {notice === 'cancelled'
              ? t('online.challenge.link.cancelledByChallenger')
              : t('online.challenge.link.expiredOrGone')}
          </p>
          <button type="button" className="challenge-invite-secondary" onClick={onLeave}>
            {t('online.challenge.link.backHome')}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'waiting') {
    return (
      <div className="challenge-invite-page">
        <div className="challenge-invite-card">
          <h2>{t('online.challenge.link.waitingChallengerTitle')}</h2>
          <div className="challenge-invite-spinner" />
          <p className="challenge-invite-note">
            {t('online.challenge.link.waitingChallenger', { name: link?.fromUsername ?? '' })}
          </p>
          <button type="button" className="challenge-invite-secondary" onClick={handleLeaveWaiting}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="challenge-invite-page">
      <div className="challenge-invite-card">
        <h2 className="challenge-invite-title">
          {t('online.challenge.link.challengesYou', { name: link?.fromUsername ?? '' })}
        </h2>
        {/* Chi non ha punteggio non lascia un paragrafo vuoto al suo posto. */}
        {link?.fromRating != null && (
          <p className="challenge-invite-rating">{link.fromRating}</p>
        )}

        {link && (
          <ChallengeDetails
            variant={link.variant}
            timeControl={link.timeControl}
            color={link.color}
            matchType={link.matchType}
            rated={link.rated}
            gameMode={link.gameMode}
            opening={link.opening}
            legs={link.legs}
            /*
             * Qui chi gioca si sa: e' chi sta guardando questa pagina.
             *
             * Il link e' l'unico posto dove «Valutata/Amichevole» ha un senso
             * mentre nessuno l'ha ancora raccolto — ma questa pagina la sta
             * guardando qualcuno, e per LUI la risposta c'e' gia'. Chi ha creato
             * il link e' per forza iscritto (il server rifiuta un
             * `challenge:create-link` da un ospite), quindi decide una cosa sola:
             * se chi legge ha un punteggio da muovere.
             *
             * E' la stessa cosa che dicono le due note qui sotto — `guestCasualNote`
             * spiega proprio che da ospite la partita sara' amichevole — che pero'
             * contraddicevano la riga del riassunto sopra di loro.
             */
            opponent={{ isGuest: notRegistered }}
          />
        )}

        {guestCasual && (
          <p className="challenge-invite-note">{t('online.challenge.link.guestCasualNote')}</p>
        )}

        {registeredOnly && (
          <p className="challenge-invite-note">{t('online.challenge.registeredOnlyNote')}</p>
        )}

        {error && (
          <p className="challenge-invite-error">
            {error in CHIAVI_ERRORE_ONLINE ? t(chiaveErroreOnline(error)) : error}
          </p>
        )}

        {/* L'errore dell'ingresso da ospite arriva già come CHIAVE, quindi si
            traduce e basta — non passa dalla mappa dei codici online qui sopra,
            che parla di partite e non di accessi. */}
        {erroreOspite && (
          <p className="challenge-invite-error" role="alert">{t(erroreOspite)}</p>
        )}

        <button
          type="button"
          className="challenge-invite-accept"
          onClick={handleAccept}
          disabled={phase === 'accepting' || registeredOnly}
        >
          {phase === 'accepting' ? <span className="challenge-invite-spinner small" /> : t('online.challenge.link.playNow')}
        </button>

        {!isAuthenticated && (
          <button type="button" className="challenge-invite-secondary" onClick={onLoginRequired}>
            {t('online.challenge.link.haveAccount')}
          </button>
        )}
      </div>
    </div>
  );
}

export default ChallengeInvitePage;
